import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import { listContractTemplates, saveContractTemplate } from '@/utils/templateStore';
import { defaultContractBody, defaultContractMeta, tokensInBody } from '@/utils/contract';
import type { ContractTemplate } from '@/types/portal';

interface SaveTemplatePanelProps {
  campaignName: string;
}

type PanelState = 'idle' | 'naming' | 'saved' | 'already_saved';

export default function SaveTemplatePanel({ campaignName }: SaveTemplatePanelProps) {
  const [state, setState] = useState<PanelState>('idle');
  const [nameInput, setNameInput] = useState('');
  const [savedTemplate, setSavedTemplate] = useState<ContractTemplate | null>(null);
  const [existingFromCampaign, setExistingFromCampaign] = useState<ContractTemplate | null>(null);
  const [loadingCheck, setLoadingCheck] = useState(true);

  const tokenCount = useMemo(() => tokensInBody(defaultContractBody).length, []);
  const defaultName = `${campaignName} Agreement`;

  const checkExisting = useCallback(async () => {
    setLoadingCheck(true);
    try {
      const all = await listContractTemplates();
      const match = all.find(
        (t) => t.origin === 'custom' && t.sourceCampaign === campaignName,
      );
      setExistingFromCampaign(match ?? null);
    } finally {
      setLoadingCheck(false);
    }
  }, [campaignName]);

  useEffect(() => {
    checkExisting();
  }, [checkExisting]);

  const startNaming = () => {
    setNameInput(defaultName);
    setState('naming');
  };

  const cancelNaming = () => {
    setState(existingFromCampaign ? 'already_saved' : 'idle');
  };

  const confirmSave = async () => {
    const name = nameInput.trim() || defaultName;
    const created = await saveContractTemplate({
      name,
      fileName: defaultContractMeta.fileName,
      fileType: defaultContractMeta.fileType,
      fileSize: defaultContractMeta.fileSize,
      body: defaultContractBody,
      sourceCampaign: campaignName,
    });
    setSavedTemplate(created);
    setExistingFromCampaign(created);
    setState('saved');
    window.setTimeout(() => setState('already_saved'), 3000);
  };

  if (loadingCheck) return null;

  if (state === 'saved' && savedTemplate) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
          <i className="ri-bookmark-3-fill" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-accent-900">Contract template saved</p>
          <p className="text-xs text-accent-700">
            &ldquo;{savedTemplate.name}&rdquo; is now in your saved contract templates — pick it on
            any new campaign.
          </p>
        </div>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50 text-sm">
          <i className="ri-check-line" />
        </span>
      </div>
    );
  }

  if (state === 'already_saved' && existingFromCampaign) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-bookmark-3-line" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground-900">
            Saved as &ldquo;{existingFromCampaign.name}&rdquo;
          </p>
          <p className="text-xs text-foreground-500">
            This campaign&apos;s agreement is already in your contract templates — reusable from the
            contract step of any new campaign.
          </p>
        </div>
        <button
          type="button"
          onClick={startNaming}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-[11px] font-label text-foreground-600 hover:bg-background-100"
        >
          <i className="ri-add-line" />
          Save another copy
        </button>
      </div>
    );
  }

  if (state === 'naming') {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
        <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-secondary-900">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-bookmark-3-line" />
          </span>
          Save as contract template
        </p>
        <p className="mb-3 text-xs text-secondary-900 leading-relaxed">
          The contract wording for this campaign — including all {tokenCount} dynamic fields — will
          be stored in your contract templates list. You can apply it to any future campaign in one
          click.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            autoFocus
            value={nameInput}
            maxLength={60}
            onChange={(event) => setNameInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                confirmSave();
              }
              if (event.key === 'Escape') cancelNaming();
            }}
            placeholder="Name this contract template"
            className="min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
          />
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" icon="ri-bookmark-3-line" onClick={confirmSave}>
              Save contract template
            </Button>
            <Button variant="ghost" size="sm" onClick={cancelNaming}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // idle state
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-600">
          <i className="ri-bookmark-3-line" />
        </span>
        <div>
          <p className="text-sm font-medium text-foreground-900">Save as contract template</p>
          <p className="text-xs text-foreground-500">
            Store this campaign&apos;s agreement for reuse on future campaigns — with all {tokenCount}{' '}
            dynamic fields intact.
          </p>
        </div>
      </div>
      <Button variant="outline" size="sm" icon="ri-bookmark-3-line" onClick={startNaming}>
        Save as contract template
      </Button>
    </div>
  );
}